import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  maintenanceInfaqService,
  CreateMaintenanceInfaqInput,
  ClosingReportInput,
} from '../services/maintenance-infaq.service';

const KEY = 'maintenance-infaq';

// ─── Provider hooks ─────────────────────────────────────────────────────

export function useMyMaintenanceInfaqs(status?: string) {
  return useQuery({
    queryKey: [KEY, 'mine', status],
    queryFn: () => maintenanceInfaqService.listMine(status),
    staleTime: 60 * 1000,
  });
}

export function useMyMaintenanceInfaq(id?: string) {
  return useQuery({
    queryKey: [KEY, 'mine', id],
    queryFn: () => maintenanceInfaqService.getMine(id!),
    enabled: !!id,
  });
}

export function useCreateMaintenanceInfaq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateMaintenanceInfaqInput) =>
      maintenanceInfaqService.create(payload),
    onSuccess: () => {
      toast.success('Maintenance infaq submitted for review');
      qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: (e: any) => {
      toast.error(e.response?.data?.message || 'Failed to submit campaign');
    },
  });
}

export function useWithdrawMaintenanceInfaq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => maintenanceInfaqService.withdraw(id),
    onSuccess: () => {
      toast.success('Campaign withdrawn');
      qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Failed to withdraw'),
  });
}

export function useSubmitClosingReport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ClosingReportInput }) =>
      maintenanceInfaqService.submitClosingReport(id, payload),
    onSuccess: () => {
      toast.success('Closing report submitted for review');
      qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Failed to submit report'),
  });
}

// ─── Admin hooks ────────────────────────────────────────────────────────

export function useAdminMaintenanceInfaqs(status?: string) {
  return useQuery({
    queryKey: [KEY, 'admin', status],
    queryFn: () => maintenanceInfaqService.adminListAll(status),
    staleTime: 60 * 1000,
  });
}

export function useAdminApproveInfaq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => maintenanceInfaqService.adminApprove(id),
    onSuccess: () => {
      toast.success('Campaign approved');
      qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Approval failed'),
  });
}

export function useAdminRejectInfaq() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      maintenanceInfaqService.adminReject(id, reason),
    onSuccess: () => {
      toast.success('Campaign rejected');
      qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Rejection failed'),
  });
}

export function useAdminApproveClosing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => maintenanceInfaqService.adminApproveClosing(id),
    onSuccess: () => {
      toast.success('Closing approved — funds disbursed');
      qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Approval failed'),
  });
}

export function useAdminRejectClosing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      maintenanceInfaqService.adminRejectClosing(id, reason),
    onSuccess: () => {
      toast.success('Closing report rejected');
      qc.invalidateQueries({ queryKey: [KEY] });
    },
    onError: (e: any) =>
      toast.error(e.response?.data?.message || 'Rejection failed'),
  });
}

// ─── Public hooks ───────────────────────────────────────────────────────

export function useOpenMaintenanceCampaigns() {
  return useQuery({
    queryKey: [KEY, 'public'],
    queryFn: () => maintenanceInfaqService.listOpenCampaigns(),
    staleTime: 60 * 1000,
  });
}

export function useMaintenanceCampaignBySlug(slug?: string) {
  return useQuery({
    queryKey: [KEY, 'public', slug],
    queryFn: () => maintenanceInfaqService.getCampaignBySlug(slug!),
    enabled: !!slug,
  });
}
